import React, { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Time } from 'roadie-ui'
import enMessages from './en.json'
import esMessages from './es.json'
import { AccountSetting } from 'types/profile'
import { getLatestAccountSetting } from 'util/profile'
import {
  fullySupportedLanguages,
  getLocaleFromLanguagePreference,
  getSupportedLanguage,
  partiallySupportedLanguages,
  supportedLanguages,
} from './languages'

type LocaleMessages = typeof enMessages

export type TranslationKey = keyof LocaleMessages
export {
  fullySupportedLanguages,
  getLocaleFromLanguagePreference,
  getSupportedLanguage,
  partiallySupportedLanguages,
  supportedLanguages,
}
type TranslationParams = Record<string, string | number>
type RichTranslationParams = Record<string, ReactNode>
type LocalizedTextProps = {
  translationKey: TranslationKey
  params?: TranslationParams
}
type LocalizedRichTextProps = {
  translationKey: TranslationKey
  params?: RichTranslationParams
}
type TranslationKeyMap = Record<string, TranslationKey>
type LocalizedTextMap<T extends TranslationKeyMap> = Record<keyof T, string>
type LocalizedTranslationsHelpers<T extends TranslationKeyMap> = {
  map: LocalizedTextMap<T>
  text: <K extends keyof T>(name: K, params?: TranslationParams) => string
  richText: <K extends keyof T>(name: K, params?: RichTranslationParams) => ReactNode
  plural: <K extends keyof T>(count: number, names: { one: K; other: K }, params?: TranslationParams) => string
}
type LocalizedTranslations<T extends TranslationKeyMap> = LocalizedTextMap<T> & LocalizedTranslationsHelpers<T>
type DateInput = Date | string | number
type TimeZone = string | { timeZoneId: string }
type TimeStringOptions = {
  omitTimezone?: boolean
}
type StatementPeriod = {
  start_time: string
  end_time: string
}

const messagesByLocale: Record<string, Partial<LocaleMessages>> = {
  en: enMessages,
  es: esMessages,
}

const tokenPattern = /\{([a-zA-Z0-9_]+)\}/g

// Indicates whether the selected language can be rendered from bundled localization files. If any localization files
// are partial translations, this will return true and English will be used as a fallback for missing keys.
export function hasLocalMessagesForLanguagePreference(languagePreference?: string) {
  if (!languagePreference) return true

  const locale = getSupportedLanguage(languagePreference)?.locale
  return Boolean(locale && messagesByLocale[locale])
}

// Looks up a message for the active locale and falls back to the English source string.
function getMessage(locale: string, key: TranslationKey) {
  return messagesByLocale[locale]?.[key] ?? enMessages[key]
}

function toDate(date: DateInput) {
  return date instanceof Date ? date : new Date(date)
}

function toLocalizedTimeForLocale(locale: string, date: DateInput, zone?: TimeZone) {
  return new Time(toDate(date).getTime(), zone, locale)
}

function formatDateForLocale(locale: string, date: DateInput, options: Intl.DateTimeFormatOptions) {
  return toDate(date).toLocaleDateString(locale, options)
}

function formatDateTimeForLocale(locale: string, date: DateInput, options: Intl.DateTimeFormatOptions) {
  return toDate(date).toLocaleString(locale, options)
}

function formatStatementPeriodTitleForLocale(locale: string, statementPeriod: StatementPeriod) {
  const { start_time: startTime, end_time: endTime } = statementPeriod
  const options = { month: 'short', day: 'numeric', year: 'numeric' } as const

  return `${formatDateForLocale(locale, startTime, options)} - ${formatDateForLocale(locale, endTime, options)}`
}

// Replaces simple string or numeric placeholders in a localized message.
function interpolate(message: string, params: TranslationParams = {}) {
  return message.replace(tokenPattern, (token, name: string) => {
    const replacement = params[name]
    return replacement === undefined ? token : String(replacement)
  })
}

// Replaces placeholders with React nodes for localized sentences that contain links or styled text.
function interpolateRich(message: string, params: RichTranslationParams = {}) {
  const nodes: Array<{ key: string; node: ReactNode }> = []
  const tokenCounts: Record<string, number> = {}
  let textIndex = 0
  let lastIndex = 0

  message.replace(tokenPattern, (token, name: string, index: number) => {
    if (index > lastIndex) {
      nodes.push({ key: `text:${textIndex}`, node: message.slice(lastIndex, index) })
      textIndex += 1
    }

    const tokenIndex = tokenCounts[name] || 0
    nodes.push({ key: `token:${name}:${tokenIndex}`, node: params[name] === undefined ? token : params[name] })
    tokenCounts[name] = tokenIndex + 1
    lastIndex = index + token.length
    return token
  })

  if (lastIndex < message.length) nodes.push({ key: `text:${textIndex}`, node: message.slice(lastIndex) })

  return (
    <>
      {nodes.map(({ key, node }) => (
        <React.Fragment key={key}>{node}</React.Fragment>
      ))}
    </>
  )
}

function mapTranslationKeys<T extends TranslationKeyMap>(
  translationKeyMap: T,
  textForKey: (translationKey: TranslationKey) => string
) {
  const localizedTextMap = {} as LocalizedTextMap<T>

  Object.entries(translationKeyMap).forEach(([name, translationKey]) => {
    localizedTextMap[name as keyof T] = textForKey(translationKey)
  })

  return localizedTextMap
}

export function createLocalizedTranslations<T extends TranslationKeyMap>(
  translationKeyMap: T,
  messageMap: LocalizedTextMap<T>
): LocalizedTranslations<T> {
  const localizedTextMap = {} as LocalizedTextMap<T>

  Object.keys(translationKeyMap).forEach((name) => {
    localizedTextMap[name as keyof T] = messageMap[name as keyof T]
  })

  const helpers: LocalizedTranslationsHelpers<T> = {
    map: localizedTextMap,
    text: <K extends keyof T>(name: K, params?: TranslationParams) => interpolate(localizedTextMap[name], params),
    richText: <K extends keyof T>(name: K, params?: RichTranslationParams) =>
      interpolateRich(localizedTextMap[name], params),
    plural: <K extends keyof T>(count: number, names: { one: K; other: K }, params?: TranslationParams) => {
      const name = count === 1 ? names.one : names.other
      return interpolate(localizedTextMap[name], { count, ...params })
    },
  }

  return Object.assign({}, localizedTextMap, helpers)
}

function createLocalizedTranslationsFromTranslator<T extends TranslationKeyMap>(
  translationKeyMap: T,
  t: (key: TranslationKey, params?: TranslationParams) => string,
  richT: (key: TranslationKey, params?: RichTranslationParams) => ReactNode
): LocalizedTranslations<T> {
  const messageMap = mapTranslationKeys(translationKeyMap, t)
  return Object.assign({}, messageMap, {
    map: messageMap,
    text: <K extends keyof T>(name: K, params?: TranslationParams) => t(translationKeyMap[name], params),
    richText: <K extends keyof T>(name: K, params?: RichTranslationParams) => richT(translationKeyMap[name], params),
    plural: <K extends keyof T>(count: number, names: { one: K; other: K }, params?: TranslationParams) => {
      const name = count === 1 ? names.one : names.other
      return t(translationKeyMap[name], { count, ...params })
    },
  })
}

// Returns translation helpers scoped to the driver's preferred language.
export function useLocalization() {
  const accountSettings = useSelector<any, AccountSetting[]>((state) => state.accountSettings || [])
  const languagePreference = getLatestAccountSetting(accountSettings, 'preferred_language')
  const locale = getLocaleFromLanguagePreference(languagePreference)

  return useMemo(
    () => ({
      locale,
      t: (key: TranslationKey, params?: TranslationParams) => interpolate(getMessage(locale, key), params),
      richT: (key: TranslationKey, params?: RichTranslationParams) => interpolateRich(getMessage(locale, key), params),
      formatDate: (date: DateInput, options: Intl.DateTimeFormatOptions) => formatDateForLocale(locale, date, options),
      formatDateTime: (date: DateInput, options: Intl.DateTimeFormatOptions) =>
        formatDateTimeForLocale(locale, date, options),
      formatTime: (date: DateInput, zone?: TimeZone, options?: TimeStringOptions) =>
        toLocalizedTimeForLocale(locale, date, zone).getTimeString(options),
      formatStatementPeriodTitle: (statementPeriod: StatementPeriod) =>
        formatStatementPeriodTitleForLocale(locale, statementPeriod),
      toLocalizedTime: (date: DateInput, zone?: TimeZone) => toLocalizedTimeForLocale(locale, date, zone),
    }),
    [locale]
  )
}

// Returns localized page text by local names while keeping interpolation in the localization layer.
export function useLocalizedTranslations<T extends TranslationKeyMap>(translationKeyMap: T): LocalizedTranslations<T> {
  const { t, richT } = useLocalization()

  return useMemo(
    () => createLocalizedTranslationsFromTranslator(translationKeyMap, t, richT),
    [richT, t, translationKeyMap]
  )
}

// Injects localization helpers into class components that cannot call hooks directly.
export function withLocalization<P extends { localization: ReturnType<typeof useLocalization> }>(
  Component: React.ComponentType<P>
) {
  const WrappedComponent = (props: Omit<P, 'localization'>) => {
    const localization = useLocalization()

    return <Component {...(props as P)} localization={localization} />
  }

  WrappedComponent.displayName = `withLocalization(${Component.displayName || Component.name || 'Component'})`

  return WrappedComponent
}

// Converts a stable map of local names to translation keys into English fallback strings outside React.
export function getEnglishTextMap<T extends TranslationKeyMap>(translationKeyMap: T): LocalizedTextMap<T> {
  return mapTranslationKeys(translationKeyMap, (translationKey) => enMessages[translationKey])
}

// Renders a localized string in JSX contexts where a hook cannot be called directly.
export function LocalizedText(props: LocalizedTextProps) {
  const { translationKey, params } = props
  const { t } = useLocalization()

  return <>{t(translationKey, params)}</>
}

// Renders a localized string with React node placeholders in JSX contexts.
export function LocalizedRichText(props: LocalizedRichTextProps) {
  const { translationKey, params } = props
  const { richT } = useLocalization()

  return <>{richT(translationKey, params)}</>
}
